<script setup lang="ts">
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
} from 'reka-ui';
import { PlusIcon } from '@heroicons/vue/24/outline';
import i18n from '@/plugins/vue-i18n';
import OrganisationSwitcherRow from '@/components/Navigation/OrganisationSwitcher/OrganisationSwitcherRow.vue';
import { useFlipReorderAnimation } from '@/composables/use-flip-reorder-animation';
import type { UserOrganisation } from '@/types/api/auth.types';

const { t } = i18n.global;

const props = defineProps<{
  open: boolean;
  orgs: UserOrganisation[];
  selectedOrgId?: string;
  showCreateNew?: boolean;
  createNewLabel?: string;
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'select-org', org: UserOrganisation): void;
  (e: 'set-primary', id: string): void;
  (e: 'create-new'): void;
}>();

// Arms the reorder animation only when the org list actually reorders after
// set-primary resolves - never plays on mount/unrelated updates.
const { setRowRef, armFlip } = useFlipReorderAnimation(() => props.orgs);

const handleSetPrimary = (id: string) => {
  armFlip();
  emit('set-primary', id);
};
</script>

<template>
  <DropdownMenuRoot :open="open" @update:open="emit('update:open', $event)">
    <DropdownMenuTrigger as-child>
      <slot name="trigger" />
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent
        :side-offset="8"
        :collision-padding="16"
        data-cy="organisation-switcher-menu"
        class="border-border-border-200 z-60 w-80 overflow-hidden rounded-lg border bg-white shadow-lg"
      >
        <div class="border-border-border-200 border-b px-4 py-3">
          <p class="type-h5 text-gray-900">
            {{ t('navigation.organisationSwitcher.title') }}
          </p>
          <p class="type-body-2 text-gray-500">
            {{ t('navigation.organisationSwitcher.subtitle') }}
          </p>
        </div>

        <div class="max-h-72 overflow-y-auto p-1" style="scrollbar-gutter: stable">
          <div>
            <OrganisationSwitcherRow
              v-for="org in orgs"
              :ref="(el) => setRowRef(org.id, el)"
              :key="org.id"
              :org="org"
              :is-selected="org.id === selectedOrgId"
              @select="emit('select-org', org)"
              @set-primary="handleSetPrimary(org.id)"
            />
          </div>
        </div>

        <button
          v-if="showCreateNew"
          type="button"
          class="border-border-border-200 type-body-1 flex w-full items-center gap-2 border-t px-4 py-3 text-left text-gray-700 hover:bg-gray-50"
          data-cy="organisation-switcher-create-new"
          @click="emit('create-new')"
        >
          <PlusIcon class="h-5 w-5 shrink-0" />
          {{ createNewLabel }}
        </button>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
